import { call, put, select, takeLatest } from 'redux-saga/effects';
import axios from '../../utils/axios';
import { BASEURL } from '../../BaseUrl/BaseUrl';
import { isLogout } from '../slices/login';
import {
  startClaimsStatsLoading,
  setClaimsStatsSuccess,
  failClaimsStats,
  startClaimsDetailLoading,
  setClaimsDetailSuccess,
  failClaimsDetail,
  startClaimsBulkAction,
  stopClaimsBulkAction,
  startClaimApprovalStatusLoading,
  stopClaimApprovalStatusLoading,
  startClaimApprovalStatusUpdating,
  stopClaimApprovalStatusUpdating,
  setClaimApprovalStatus,
} from '../slices/claimsSlice';
import { hasError, getErrorMessage, getSuccessMessage, setResponse } from '../slices/responseStatus';
import sagaActions from '../actions';

function authHeaders() {
  const Token = window.localStorage.getItem('auth-token');
  return { headers: { 'x-auth-token': `${Token}` } };
}

export function* fetchClaimsStatsSaga(action = {}) {
  const page = action.page != null ? Number(action.page) : 1;
  const limit = action.limit != null ? Number(action.limit) : 20;
  const safePage = Number.isFinite(page) && page > 0 ? page : 1;
  const safeLimit = Number.isFinite(limit) && limit > 0 ? limit : 20;
  const productId = action.product_id != null ? String(action.product_id) : '';
  const productIdTrim = typeof productId === 'string' ? productId.trim() : '';
  const campaignId = action.campaign_id != null ? String(action.campaign_id) : '';
  const minClaims = action.min != null && action.min !== '' ? Number(action.min) : null;
  const maxClaims = action.max != null && action.max !== '' ? Number(action.max) : null;

  try {
    yield put(hasError(false));
    yield put(startClaimsStatsLoading());
    const params = [
      `page=${encodeURIComponent(safePage)}`,
      `limit=${encodeURIComponent(safeLimit)}`,
      `product_id=${encodeURIComponent(productIdTrim)}`,
      `campaign_id=${encodeURIComponent(campaignId)}`,
    ];
    if (Number.isFinite(minClaims) && Number.isFinite(maxClaims)) {
      params.push(`min=${encodeURIComponent(minClaims)}`, `max=${encodeURIComponent(maxClaims)}`);
    }
    const q = params.join('&');
    const response = yield call(() => axios.get(`${BASEURL}/claims/stats?${q}`, authHeaders()));
    const block = response?.data?.data || {};
    const pagination = block.pagination || {};
    const farmers = Array.isArray(block.farmers) ? block.farmers : [];

    yield put(
      setClaimsStatsSuccess({
        stats: block.stats || {},
        farmers,
        totalFarmers: Number(pagination.total) || 0,
        page: Number(pagination.page) > 0 ? Number(pagination.page) : safePage,
        limit: Number(pagination.limit) > 0 ? Number(pagination.limit) : safeLimit,
      })
    );
  } catch (error) {
    yield put(failClaimsStats());
    yield put(hasError(true));
    yield put(getErrorMessage(error?.error?.message || error?.message || 'Failed to load claims stats'));
    if (error?.error?.statusCode === 401) {
      yield put(isLogout(true));
    }
  }
}

export function* fetchClaimsByMobileSaga(action) {
  const raw = action?.mobileNumber;
  const mobileNumber = raw != null ? String(raw).trim() : '';
  if (!mobileNumber) return;

  try {
    yield put(hasError(false));
    yield put(startClaimsDetailLoading());
    const encoded = encodeURIComponent(mobileNumber);
    const response = yield call(() => axios.get(`${BASEURL}/claims/${encoded}`, authHeaders()));
    const block = response?.data?.data;
    const list = Array.isArray(block?.claims) ? block.claims : [];
    yield put(setClaimsDetailSuccess({ list, mobileNumber }));
  } catch (error) {
    yield put(failClaimsDetail());
    yield put(hasError(true));
    yield put(getErrorMessage(error?.error?.message || error?.message || 'Failed to load claim details'));
    if (error?.error?.statusCode === 401) {
      yield put(isLogout(true));
    }
  }
}

export function* claimsBulkActionSaga(action) {
  const { claimIds, status } = action;
  const ids = Array.isArray(claimIds) ? claimIds.filter(Boolean) : [];
  if (!ids.length || (status !== 'approve' && status !== 'reject')) return;

  try {
    yield put(hasError(false));
    yield put(startClaimsBulkAction());
    const response = yield call(() =>
      axios.post(`${BASEURL}/claims/bulk-action?status=${encodeURIComponent(status)}`, { claimIds: ids }, authHeaders())
    );
    yield put(stopClaimsBulkAction());
    yield put(getSuccessMessage(response?.data?.meta?.message || 'Claims updated'));
    yield put(setResponse(response.status));

    const mobile = yield select((s) => s.claims.claimsDetailMobile);
    if (mobile) {
      yield put({ type: sagaActions.GET_CLAIMS_BY_MOBILE, mobileNumber: mobile });
    }
    const listQuery = yield select((s) => s.claims.claimsStatsListQuery);
    yield put({
      type: sagaActions.GET_CLAIMS_STATS,
      page: listQuery?.page ?? 1,
      limit: listQuery?.limit ?? 20,
    });
  } catch (error) {
    yield put(stopClaimsBulkAction());
    yield put(hasError(true));
    yield put(getErrorMessage(error?.error?.message || error?.message || 'Bulk action failed'));
    if (error?.error?.statusCode === 401) {
      yield put(isLogout(true));
    }
  }
}

export function* getClaimApprovalStatusSaga() {
  try {
    yield put(hasError(false));
    yield put(startClaimApprovalStatusLoading());
    const response = yield call(() => axios.get(`${BASEURL}/claims/approval-status`, authHeaders()));
    const raw = response?.data?.data?.status;
    yield put(setClaimApprovalStatus(raw));
  } catch (error) {
    yield put(hasError(true));
    yield put(getErrorMessage(error?.error?.message || error?.message || 'Failed to load claim approval status'));
    if (error?.error?.statusCode === 401) {
      yield put(isLogout(true));
    }
  } finally {
    yield put(stopClaimApprovalStatusLoading());
  }
}

export function* updateClaimApprovalStatusSaga(action) {
  const status = action?.payload?.status;
  if (status !== '0' && status !== '1') return;

  try {
    yield put(hasError(false));
    yield put(startClaimApprovalStatusUpdating());
    const response = yield call(() => axios.put(`${BASEURL}/claims/approval-status`, { status }, authHeaders()));
    yield put(setClaimApprovalStatus(status));
    yield put(getSuccessMessage(response?.data?.meta?.message || 'Status updated'));
    yield put(setResponse(response.status));
  } catch (error) {
    yield put(hasError(true));
    yield put(getErrorMessage(error?.error?.message || error?.message || 'Failed to update claim approval status'));
    if (error?.error?.statusCode === 401) {
      yield put(isLogout(true));
    }
  } finally {
    yield put(stopClaimApprovalStatusUpdating());
  }
}

export function* claimsStatsSaga() {
  yield takeLatest(sagaActions.GET_CLAIMS_STATS, fetchClaimsStatsSaga);
  yield takeLatest(sagaActions.GET_CLAIMS_BY_MOBILE, fetchClaimsByMobileSaga);
  yield takeLatest(sagaActions.CLAIMS_BULK_ACTION, claimsBulkActionSaga);
  yield takeLatest(sagaActions.GET_CLAIM_APPROVAL_STATUS, getClaimApprovalStatusSaga);
  yield takeLatest(sagaActions.UPDATE_CLAIM_APPROVAL_STATUS, updateClaimApprovalStatusSaga);
}
